import { mergeSkillsContexts } from '../chat/utils/mergeSkillsContexts';
let promptIdCounter = 0;
export class BreezeEngineRegistry {
  constructor() {
    this._tools = new Map();
    this._cachedToolSpecs = null;
    this._skillSpecs = new Map();
    this._cachedSkillSpecs = null;
    this._conversationMetadata = new Map();
    this._messageContexts = new Map();
    this._context = new Map();
    this._queuedPrompt = null;
    this._requestedConversationId = null;
    this._requestedConversationInstanceId = undefined;
    this._shouldStartNewConversation = false;
    this._startNewConversationInstanceId = undefined;
    this._isOpen = false;
    this._listeners = new Set();
    this._panelListeners = new Set();
  }
  get isOpen() {
    return this._isOpen;
  }
  openPanel(message, options) {
    this._isOpen = true;
    if (message !== undefined) {
      this._queuedPrompt = Object.assign({
        id: String(++promptIdCounter),
        message
      }, options);
      this.notify();
    }
    this.notifyPanelListeners(options === null || options === void 0 ? void 0 : options.instanceId);
  }

  /**
   * Opens the panel and signals mounted chat sessions to switch to a specific
   * conversation. Handled by useChatManager (panel already open) and
   * BreezeEngineThreadsInit (panel opening fresh).
   */
  openConversation(conversationId, instanceId) {
    this._isOpen = true;
    this._requestedConversationId = conversationId;
    this._requestedConversationInstanceId = instanceId;
    this.notify();
    this.notifyPanelListeners(instanceId);
  }
  getRequestedConversationId() {
    return this._requestedConversationId;
  }
  getRequestedConversationInstanceId() {
    return this._requestedConversationInstanceId;
  }
  clearRequestedConversationId() {
    this._requestedConversationId = null;
    this._requestedConversationInstanceId = undefined;
    // No notify() — clearing doesn't need to trigger listeners.
  }

  /**
   * Signals mounted chat sessions to start a new conversation without a
   * message. Handled by useChatManager (panel already open) and
   * BreezeEngineThreadsInit (panel opening fresh). Mirrors the
   * openConversation / getRequestedConversationId pattern.
   */
  startNewConversation(instanceId) {
    this._isOpen = true;
    this._shouldStartNewConversation = true;
    this._startNewConversationInstanceId = instanceId;
    this.notify();
    this.notifyPanelListeners(instanceId);
  }
  getShouldStartNewConversation() {
    return this._shouldStartNewConversation;
  }
  getStartNewConversationInstanceId() {
    return this._startNewConversationInstanceId;
  }
  clearShouldStartNewConversation() {
    this._shouldStartNewConversation = false;
    this._startNewConversationInstanceId = undefined;
    // No notify() — clearing doesn't need to trigger listeners.
  }
  closePanel() {
    this._isOpen = false;
    this.notifyPanelListeners();
  }
  getQueuedPrompt() {
    return this._queuedPrompt;
  }
  clearQueuedPrompt() {
    this._queuedPrompt = null;
    this.notify();
  }
  registerTool(def) {
    this._tools.set(def.name, def);
    this._cachedToolSpecs = null;
    this.notify();
  }
  unregisterTool(name) {
    this._tools.delete(name);
    this._cachedToolSpecs = null;
    this.notify();
  }
  hasTool(name) {
    return this._tools.has(name);
  }
  executeTool(name, args, toolCallId) {
    const tool = this._tools.get(name);
    if (!tool) {
      return Promise.reject(new Error(`Tool '${name}' not registered`));
    }
    try {
      return Promise.resolve(tool.onToolUse(args, toolCallId));
    } catch (err) {
      return Promise.reject(err);
    }
  }
  getExternalToolSpecs() {
    if (!this._cachedToolSpecs) {
      this._cachedToolSpecs = [...this._tools.values()].map(t => ({
        name: t.name,
        description: t.description,
        inputSchema: JSON.stringify(t.inputSchema)
      }));
    }
    return this._cachedToolSpecs;
  }
  registerSkillSpec(spec) {
    this._skillSpecs.set(spec.name, spec);
    this._cachedSkillSpecs = null;
    this.notify();
  }
  unregisterSkillSpec(name) {
    this._skillSpecs.delete(name);
    this._cachedSkillSpecs = null;
    this.notify();
  }
  getRegistrySkillSpecs() {
    if (!this._cachedSkillSpecs) {
      this._cachedSkillSpecs = [...this._skillSpecs.values()];
    }
    return this._cachedSkillSpecs;
  }
  registerConversationMetadata(item) {
    this._conversationMetadata.set(item.contextKey, item);
    this.notify();
  }
  unregisterConversationMetadata(key) {
    this._conversationMetadata.delete(key);
    this.notify();
  }
  getConversationMetadata() {
    return [...this._conversationMetadata.values()];
  }
  registerContext(key, context) {
    this._context.set(key, context);
  }
  unregisterContext(key) {
    this._context.delete(key);
  }
  getContextEntry(key) {
    return this._context.get(key);
  }
  getContextKeys() {
    return [...this._context.keys()];
  }
  getContext() {
    const merged = {};
    for (const ctx of this._context.values()) {
      for (const [k, value] of Object.entries(ctx)) {
        merged[k] = value;
      }
    }
    return merged;
  }
  registerMessageContexts(key, contexts) {
    this._messageContexts.set(key, contexts);
  }
  unregisterMessageContexts(key) {
    this._messageContexts.delete(key);
  }
  getMessageContexts() {
    return mergeSkillsContexts([...this._messageContexts.values()].flat());
  }
  addListener(callback) {
    this._listeners.add(callback);
    return () => {
      this._listeners.delete(callback);
    };
  }
  subscribeToPanelState(callback) {
    this._panelListeners.add(callback);
    return () => {
      this._panelListeners.delete(callback);
    };
  }
  notify() {
    const snapshot = [...this._listeners];
    for (const listener of snapshot) {
      listener();
    }
  }
  notifyPanelListeners(instanceId) {
    const snapshot = [...this._panelListeners];
    for (const listener of snapshot) {
      listener(this._isOpen, instanceId);
    }
  }
}
function resolveRegistryWindow() {
  let top = window;
  let current = window;
  while (current.parent && current.parent !== current) {
    try {
      // Accessing .document throws for cross-origin frames — use it as the
      // same-origin test before walking up to the parent.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      current.parent.document;
      top = current.parent;
    } catch (_unused) {
      break;
    }
    current = current.parent;
  }
  return top;
}
const registryWindow = resolveRegistryWindow();
if (!registryWindow.BreezeEngineRegistry) {
  registryWindow.BreezeEngineRegistry = new BreezeEngineRegistry();
}
export const breezeEngineRegistry = registryWindow.BreezeEngineRegistry;